import { buildBodyParentMap } from "../../utils/bodyHierarchy";
import { resolveJointUuidFromRef } from "../../utils/jointUtils";
import { estimateLabelWidth } from "./helpers";
import {
  SATELLITE_Y_GAP,
  BASE_LEVEL_GAP,
  MIN_LEVEL_SPACING,
  CHILD_SPACING,
  ROOT_SPACING,
  FLAT_GRID_X_GAP,
  FLAT_GRID_Y_GAP,
  ROOT_OBJECT_NODE_Y_OFFSET,
  ACT_INDENT,
  ACT_Y_START,
  ACT_Y_GAP,
} from "./constants";

export function buildLayoutContext({
  bodyTree,
  bodies,
  effectiveWorldBodies,
  joints,
  cameras,
  actuators,
  tendons,
  effectiveHiddenTypes,
}) {
  const hiddenTypes = effectiveHiddenTypes;
  const bodiesHidden = hiddenTypes.has("body");
  const satelliteYGap = bodiesHidden ? 22 : SATELLITE_Y_GAP;
  const baseLevelGap = bodiesHidden ? 42 : BASE_LEVEL_GAP;
  const minLevelSpacing = bodiesHidden ? 56 : MIN_LEVEL_SPACING;
  const childSpacing = bodiesHidden ? 24 : CHILD_SPACING;
  const rootSpacing = bodiesHidden ? 42 : ROOT_SPACING;
  const flatGridXGap = bodiesHidden ? 24 : FLAT_GRID_X_GAP;
  const flatGridYGap = bodiesHidden ? 28 : FLAT_GRID_Y_GAP;
  const actuatorXOffset = bodiesHidden ? 0 : ACT_INDENT;
  const actuatorYStart = bodiesHidden ? 34 : ACT_Y_START;
  const actuatorYGap = bodiesHidden ? 30 : ACT_Y_GAP;

  const bodyMap = new Map();
  const buildBodyMap = (tree) => {
    tree.forEach((body) => {
      if (!body?.uuid) return;
      bodyMap.set(body.uuid, body);
      if (body.children?.length) buildBodyMap(body.children);
    });
  };
  buildBodyMap(bodyTree);

  const parentByChildUuid = buildBodyParentMap(bodies || {});
  const isRootBodyUuid = (bodyUuid) => bodyUuid && parentByChildUuid[bodyUuid] == null;
  const isFreeJointUuid = (jointUuid) => joints?.[jointUuid]?.kind === "free";
  const bodyHasFreeJoint = (body) => {
    if (!Array.isArray(body?.joints)) return false;
    return body.joints.some((ref) => {
      const id = typeof ref === "string" ? ref : ref?.uuid;
      return !!id && isFreeJointUuid(id);
    });
  };

  const bodyLabelWidths = Array.from(bodyMap.values()).map((body) =>
    estimateLabelWidth(body?.name ?? "")
  );
  const maxBodyLabelWidth = bodyLabelWidths.length > 0 ? Math.max(...bodyLabelWidths) : 0;
  const uniformSatXOffset = bodiesHidden ? 24 : Math.max(80, 35 + maxBodyLabelWidth + 20);

  const mountedCamerasByBody = new Map();
  const worldCameras = [];
  Object.values(cameras ?? {}).forEach((camera) => {
    if (camera.kind === "mounted" && camera.mountBodyUuid) {
      const list = mountedCamerasByBody.get(camera.mountBodyUuid) ?? [];
      list.push(camera);
      mountedCamerasByBody.set(camera.mountBodyUuid, list);
    } else {
      worldCameras.push(camera);
    }
  });

  const tendonByNamePre = new Map();
  Object.values(tendons ?? {}).forEach((tendon) => {
    if (tendon?.name) tendonByNamePre.set(tendon.name, tendon);
  });

  const countSubs = (body) => {
    let n = 0;
    if (!hiddenTypes.has("mesh") && Array.isArray(body.geoms))
      n += body.geoms.filter((geom) => geom?.visualOnly === true).length;
    if (!hiddenTypes.has("geom") && Array.isArray(body.geoms))
      n += body.geoms.filter((geom) => geom && geom.visualOnly !== true).length;
    if (!hiddenTypes.has("site") && Array.isArray(body.sites)) n += body.sites.length;
    if (!hiddenTypes.has("inertial") && body.inertia) n += 1;
    if (!hiddenTypes.has("camera")) n += mountedCamerasByBody.get(body.uuid)?.length ?? 0;
    if (!hiddenTypes.has("joint") && Array.isArray(body.joints)) {
      const parent = parentByChildUuid[body.uuid];
      if (parent == null) {
        n += body.joints.filter((ref) => {
          const id = typeof ref === "string" ? ref : ref?.uuid;
          return !!id && !isFreeJointUuid(id);
        }).length;
      }
    }
    return n;
  };

  const satHalfH = (body) => {
    const n = countSubs(body);
    return n > 1 ? ((n - 1) * satelliteYGap) / 2 : 0;
  };

  const actuatorCountByJoint = new Map();
  Object.values(actuators ?? {}).forEach((actuator) => {
    const tendonUuid =
      actuator.tendonUuid || tendonByNamePre.get(actuator.tendon || "")?.uuid || null;
    const jointRef = typeof actuator.joint === "string" ? actuator.joint : null;
    const resolvedJointUuid = jointRef ? resolveJointUuidFromRef(jointRef, joints ?? {}) : null;

    if (resolvedJointUuid) {
      actuatorCountByJoint.set(
        resolvedJointUuid,
        (actuatorCountByJoint.get(resolvedJointUuid) ?? 0) + 1
      );
      return;
    }

    if (tendonUuid && tendons?.[tendonUuid]) {
      const tendon = tendons[tendonUuid];
      const firstRef = Array.isArray(tendon.joints) ? tendon.joints[0] : null;
      const jointUuid = typeof firstRef === "string" ? firstRef : (firstRef?.joint ?? null);
      if (jointUuid) {
        actuatorCountByJoint.set(jointUuid, (actuatorCountByJoint.get(jointUuid) ?? 0) + 1);
      }
    }
  });

  const jointActHalfH = (jointUuid) => {
    if (hiddenTypes.has("actuator") || hiddenTypes.has("joint")) return 0;
    const n = actuatorCountByJoint.get(jointUuid) ?? 0;
    if (n === 0) return 0;
    const totalExtent = actuatorYStart + (n - 1) * actuatorYGap;
    return Math.max(0, totalExtent - 24);
  };

  const bodyJointActHalfH = (body) => {
    if (!Array.isArray(body.joints) || body.joints.length === 0) return 0;
    return Math.max(...body.joints.map(jointActHalfH));
  };

  const widthCache = new Map();
  const getSubtreeWidth = (body) => {
    if (!body?.uuid) return 100;
    if (widthCache.has(body.uuid)) return widthCache.get(body.uuid);
    const bodyLabelRight = bodiesHidden ? 0 : 35 + estimateLabelWidth(body.name);
    const nodeW =
      countSubs(body) === 0
        ? 25 + bodyLabelRight + 10
        : 25 + Math.max(bodyLabelRight + 10, uniformSatXOffset + 9 + 5 + 18 * 6.5);
    if (!body.children?.length) {
      widthCache.set(body.uuid, nodeW);
      return nodeW;
    }
    const childWidths = body.children.map((child) => getSubtreeWidth(child));
    const total =
      childWidths.reduce((sum, width) => sum + width, 0) +
      (body.children.length - 1) * childSpacing;
    const width = Math.max(nodeW, total);
    widthCache.set(body.uuid, width);
    return width;
  };

  const rootObjectLabelHeight = hiddenTypes.has("object") ? 0 : ROOT_OBJECT_NODE_Y_OFFSET + 54;
  const getFlatRootHeight = (body) =>
    Math.max(
      54,
      rootObjectLabelHeight,
      satHalfH(body) * 2 + 56,
      bodiesHidden ? satHalfH(body) + bodyJointActHalfH(body) + 70 : 0
    );

  const rightContourCache = new Map();
  const computeRightContour = (body) => {
    if (rightContourCache.has(body.uuid)) return rightContourCache.get(body.uuid);
    const bodyLabelRight = bodiesHidden ? 0 : 35 + estimateLabelWidth(body.name);
    const ownRight =
      countSubs(body) === 0
        ? bodiesHidden
          ? 25
          : bodyLabelRight + 5
        : Math.max(bodyLabelRight + 5, uniformSatXOffset + 9 + 5 + 18 * 6.5);
    if (!body.children?.length) {
      const contour = [ownRight];
      rightContourCache.set(body.uuid, contour);
      return contour;
    }
    const childWidths = body.children.map((child) => getSubtreeWidth(child));
    const total =
      childWidths.reduce((sum, width) => sum + width, 0) +
      (body.children.length - 1) * childSpacing;
    let cursorX = -total / 2;
    const childOffsets = body.children.map((_, index) => {
      const offset = cursorX + childWidths[index] / 2;
      cursorX += childWidths[index] + childSpacing;
      return offset;
    });
    const maxChildDepth = body.children.reduce(
      (maxDepth, child) => Math.max(maxDepth, computeRightContour(child).length),
      0
    );
    const contour = [ownRight];
    for (let depth = 0; depth < maxChildDepth; depth++) {
      let maxRight = -Infinity;
      body.children.forEach((child, index) => {
        const childContour = computeRightContour(child);
        if (depth < childContour.length)
          maxRight = Math.max(maxRight, childOffsets[index] + childContour[depth]);
      });
      if (maxRight > -Infinity) contour.push(maxRight);
    }
    rightContourCache.set(body.uuid, contour);
    return contour;
  };

  const leftContourCache = new Map();
  const computeLeftContour = (body) => {
    if (leftContourCache.has(body.uuid)) return leftContourCache.get(body.uuid);
    const ownLeft = bodiesHidden ? 0 : -25;
    if (!body.children?.length) {
      const contour = [ownLeft];
      leftContourCache.set(body.uuid, contour);
      return contour;
    }
    const childWidths = body.children.map((child) => getSubtreeWidth(child));
    const total =
      childWidths.reduce((sum, width) => sum + width, 0) +
      (body.children.length - 1) * childSpacing;
    let cursorX = -total / 2;
    const childOffsets = body.children.map((_, index) => {
      const offset = cursorX + childWidths[index] / 2;
      cursorX += childWidths[index] + childSpacing;
      return offset;
    });
    const maxChildDepth = body.children.reduce(
      (maxDepth, child) => Math.max(maxDepth, computeLeftContour(child).length),
      0
    );
    const contour = [ownLeft];
    for (let depth = 0; depth < maxChildDepth; depth++) {
      let minLeft = Infinity;
      body.children.forEach((child, index) => {
        const childContour = computeLeftContour(child);
        if (depth < childContour.length)
          minLeft = Math.min(minLeft, childOffsets[index] + childContour[depth]);
      });
      if (minLeft < Infinity) contour.push(minLeft);
    }
    leftContourCache.set(body.uuid, contour);
    return contour;
  };

  const contourSibSep = (bodyA, bodyB, minSpacing) => {
    const rightA = computeRightContour(bodyA);
    const leftB = computeLeftContour(bodyB);
    let sep = minSpacing;
    const depth = Math.min(rightA.length, leftB.length);
    for (let i = 0; i < depth; i++) {
      sep = Math.max(sep, rightA[i] - leftB[i] + minSpacing);
    }
    return sep;
  };

  const rootBodyToGlobalName = new Map();
  effectiveWorldBodies.forEach((worldBody) => {
    const uuid = typeof worldBody === "string" ? worldBody : worldBody?.uuid;
    const name = typeof worldBody === "string" ? null : worldBody?.name;
    if (uuid && name) rootBodyToGlobalName.set(uuid, name);
  });

  return {
    hiddenTypes,
    bodiesHidden,
    satelliteYGap,
    baseLevelGap,
    minLevelSpacing,
    childSpacing,
    rootSpacing,
    flatGridXGap,
    flatGridYGap,
    actuatorXOffset,
    actuatorYStart,
    actuatorYGap,
    bodyMap,
    parentByChildUuid,
    isRootBodyUuid,
    isFreeJointUuid,
    bodyHasFreeJoint,
    uniformSatXOffset,
    mountedCamerasByBody,
    worldCameras,
    countSubs,
    satHalfH,
    bodyJointActHalfH,
    getSubtreeWidth,
    getFlatRootHeight,
    computeRightContour,
    contourSibSep,
    rootBodyToGlobalName,
  };
}
